/**
 * mixpanelService — app analytics, behind a consent gate AND a lazy SDK load.
 *
 * mixpanel-browser is a large dependency that only ever matters to a user who
 * has ticked the optional "analytics" consent box, yet a static import put it in
 * the initial bundle for everyone. It is therefore pulled in with a dynamic
 * import() at consent time: it ships as its own chunk, and a user who never
 * consents never downloads it at all.
 *
 * The public API is unchanged and still entirely synchronous — no call site
 * awaits anything, and no import changes. Calls made in the window between
 * "consent granted" and "SDK chunk resolved" are QUEUED and replayed in order
 * once the SDK is live; their payloads are built at call time, so timestamps,
 * $created and the identify-before-track ordering are the caller's, not the
 * flush's. Calls made without consent are still dropped on the floor, exactly
 * as before.
 */

// The SDK itself, once its chunk has resolved. null until then — every use of it
// therefore goes through enqueue(), never directly.
let mixpanel = null;

let initRequested = false; // init() ran and a token exists (load is in flight/done)
let isInitialized = false; // the chunk resolved AND mixpanel.init() has run
let loadFailed = false;    // chunk blocked (ad blockers do block Mixpanel) or init threw
let currentUser = null;

// Analytics consent gate. NOTHING is sent to (or persisted by) Mixpanel until
// the user has ticked the optional "analytics" consent box. setAnalyticsConsent()
// is the single switch that flips this — every tracking function below also
// re-checks it, so no call site can leak data ahead of consent (PDPA/PDPO).
let analyticsAllowed = false;

// Work issued while the SDK chunk was still in flight, replayed in order on
// load. Capped so a chunk that never arrives can't grow this without bound.
const MAX_PENDING_ACTIONS = 100;
const pendingActions = [];

/** Run an SDK call, keeping analytics failures non-fatal for the app. */
function run(label, action) {
  try {
    action();
  } catch (error) {
    console.error(`Mixpanel: Failed to ${label}:`, error);
  }
}

/** Run now if the SDK is live, otherwise hold it until the chunk resolves. */
function enqueue(label, action) {
  if (isInitialized) {
    run(label, action);
    return;
  }

  if (pendingActions.length >= MAX_PENDING_ACTIONS) {
    console.warn(`Mixpanel: queue full while SDK loads, dropping "${label}"`);
    return;
  }

  pendingActions.push({ label, action });
}

function flushPending() {
  const queued = pendingActions.splice(0, pendingActions.length);
  queued.forEach(({ label, action }) => run(label, action));
}

/**
 * Load and initialize Mixpanel.
 *
 * Idempotent. Initializes with opt_out_tracking_by_default so the SDK neither
 * sends events nor writes its distinct_id to localStorage until we explicitly
 * call opt_in_tracking() (done below, once consent is granted).
 * Not called at import time — only lazily from setAnalyticsConsent(true), which
 * is also the moment the network request for the SDK chunk is made.
 */
export function init() {
  if (initRequested) return;

  const token = process.env.REACT_APP_MIXPANEL_TOKEN;

  if (!token) {
    console.warn('MIXPANEL_TOKEN not found in environment. Mixpanel tracking disabled.');
    return;
  }

  initRequested = true;

  import(/* webpackChunkName: "mixpanel" */ 'mixpanel-browser')
    .then((module) => {
      mixpanel = module.default || module;

      mixpanel.init(token, {
        debug: process.env.NODE_ENV === 'development', // Enable debug in development
        track_pageview: false, // We'll track manually
        persistence: 'localStorage',
        // Compliance: do not store or transmit anything until the user opts in.
        opt_out_tracking_by_default: true,
      });
      isInitialized = true;
      console.log('Mixpanel initialized successfully');

      // Consent may have been withdrawn while the chunk was in flight. In that
      // case the SDK simply stays opted out and the queue is already empty.
      if (analyticsAllowed) {
        run('opt in', () => mixpanel.opt_in_tracking());
        flushPending();
      } else {
        pendingActions.length = 0;
      }
    })
    .catch((error) => {
      console.error('Failed to load/initialize Mixpanel:', error);
      loadFailed = true;
      pendingActions.length = 0;
    });
}

/**
 * Set the analytics-consent gate. This is the ONLY entry point that allows data
 * to start flowing to Mixpanel.
 *
 * @param {boolean} allowed - True if the user has consented to analytics.
 *   - true:  lazily load + init (if needed) and opt the SDK in.
 *   - false: opt out, clear any persisted identifiers, and block all tracking.
 */
export function setAnalyticsConsent(allowed) {
  if (allowed) {
    // Set before init() so the load handler above sees the granted consent even
    // if the chunk resolves in the same tick.
    analyticsAllowed = true;
    init();
    if (!initRequested) {
      analyticsAllowed = false; // no token — stay disabled
      return;
    }
    // If the SDK is already live, opt in now; otherwise the load handler does it.
    if (isInitialized) {
      run('opt in', () => mixpanel.opt_in_tracking());
    }
  } else {
    analyticsAllowed = false;
    // Anything queued was queued under a consent that no longer holds.
    pendingActions.length = 0;
    if (isInitialized) {
      run('opt out', () => {
        // Stops tracking AND clears the persisted distinct_id / super properties.
        mixpanel.opt_out_tracking();
        mixpanel.reset();
      });
    }
    currentUser = null;
  }
}

/**
 * Whether tracking is currently permitted (a token exists, the SDK is loading or
 * loaded, and the user has consented). True during the load window too — that is
 * what lets calls queue instead of being lost.
 */
function canTrack() {
  return initRequested && !loadFailed && analyticsAllowed;
}

/**
 * Identify user by email and create/update user profile
 * @param {string} email - User email (distinct identifier)
 * @param {object} properties - Optional initial user properties
 */
export function identify(email, properties = {}) {
  if (!canTrack()) return;

  if (!email) {
    console.warn('Mixpanel: Cannot identify user without email');
    return;
  }

  // Set currentUser FIRST before queueing mixpanel.identify
  // This prevents race condition where trackEvent might be called before identify completes
  currentUser = email;

  // Always set $name and $email for user profile
  const userProfile = {
    $email: email,
    $name: properties.name || email,
    ...properties
  };

  // $created is stamped now, not at flush time, so a queued identify still
  // records when the user was actually first seen.
  const firstSeen = {
    $email: email,
    $created: new Date().toISOString(),
    signup_method: properties.signup_method || 'unknown',
  };

  enqueue('identify user', () => {
    // Identify the user
    mixpanel.identify(email);

    // Create or update user profile using set_once (doesn't overwrite existing values)
    // and set (updates current values)
    mixpanel.people.set_once(firstSeen);

    // Update current user properties
    mixpanel.people.set(userProfile);

    console.log('Mixpanel: User identified and profile updated:', email);
  });
}

/**
 * Track an event with properties
 * @param {string} eventName - Human-readable event name (e.g., "Message Sent")
 * @param {object} properties - Event properties (use snake_case)
 */
export function trackEvent(eventName, properties = {}) {
  if (!canTrack()) return;

  if (!currentUser) {
    console.warn('Mixpanel: Cannot track event before user is identified. Call identify() first.');
    return;
  }

  // Built here, not in the queued callback: a queued event must carry the
  // timestamp of the moment it happened, not of the moment the SDK arrived.
  const eventData = {
    ...properties,
    timestamp: Date.now(),
    environment: process.env.NODE_ENV || 'development',
  };

  enqueue(`track event "${eventName}"`, () => mixpanel.track(eventName, eventData));
}

/**
 * Set or update user properties
 * @param {object} properties - User properties to set (use snake_case)
 */
export function setUserProperties(properties = {}) {
  if (!canTrack()) return;

  if (!currentUser) {
    console.warn('Mixpanel: User not identified, cannot set properties');
    return;
  }

  const snapshot = { ...properties };
  enqueue('set user properties', () => mixpanel.people.set(snapshot));
}

/**
 * Increment a user property by a value
 * @param {string} property - Property name
 * @param {number} value - Value to increment by (default 1)
 */
export function incrementUserProperty(property, value = 1) {
  if (!canTrack()) return;

  if (!currentUser) {
    console.warn('Mixpanel: User not identified, cannot increment property');
    return;
  }

  enqueue(`increment property "${property}"`, () => mixpanel.people.increment(property, value));
}

/**
 * Build user properties object from user data
 * @param {object} user - User object
 * @param {object} userStats - User stats object (optional)
 * @returns {object} User properties for Mixpanel
 */
export function buildUserProperties(user, userStats = null) {
  if (!user) return {};

  const properties = {
    // Demographic properties
    email: user.email,
    signup_date: user.createdAt || new Date().toISOString(),
    signup_method: 'thinkific_sso',
  };

  // Add user profile data
  if (user.financial_experience) properties.financial_experience = user.financial_experience;
  if (user.money_stage) properties.money_stage = user.money_stage;
  if (user.monthly_savings) properties.monthly_savings = user.monthly_savings;
  if (user.age_range) properties.age_range = user.age_range;
  if (user.countries) properties.countries = user.countries;
  if (user.thinkific_id) properties.thinkific_id = user.thinkific_id;
  if (user.currentMission) properties.current_mission = user.currentMission;

  // Add user stats if provided
  if (userStats) {
    if (userStats.journey_start_date) properties.journey_start_date = userStats.journey_start_date;
    if (userStats.days_active !== undefined) properties.days_active = userStats.days_active;
    if (userStats.total_sessions !== undefined) properties.total_sessions = userStats.total_sessions;
    if (userStats.last_active_date) properties.last_active_date = userStats.last_active_date;
    if (userStats.engagement_trend) properties.engagement_trend = userStats.engagement_trend;
    if (userStats.todos_completed !== undefined) properties.todos_completed = userStats.todos_completed;
    if (userStats.articles_read !== undefined) properties.articles_read = userStats.articles_read;
    if (userStats.videos_watched !== undefined) properties.videos_watched = userStats.videos_watched;

    // Calculate journey stage
    if (userStats.days_active !== undefined) {
      properties.journey_stage = getJourneyStage(userStats.days_active);
    }

    // Calculate days since signup
    if (userStats.journey_start_date) {
      properties.days_since_signup = getDaysSinceSignup(userStats.journey_start_date);
    }
  }

  return properties;
}

/**
 * Calculate journey stage from days active
 * @param {number} daysActive
 * @returns {string} Journey stage
 */
export function getJourneyStage(daysActive) {
  if (daysActive <= 3) return 'new';
  if (daysActive <= 7) return 'early';
  if (daysActive <= 13) return 'regular';
  return 'established';
}

/**
 * Calculate days since signup
 * @param {string|Date} journeyStartDate
 * @returns {number} Days since signup
 */
export function getDaysSinceSignup(journeyStartDate) {
  if (!journeyStartDate) return 0;
  const signupDate = new Date(journeyStartDate);
  const today = new Date();
  return Math.floor((today - signupDate) / (1000 * 60 * 60 * 24));
}

/**
 * Reset Mixpanel tracking (on logout)
 *
 * Always drops the local identity and anything still queued — replaying the
 * previous user's events after a logout would misattribute them — and resets the
 * SDK too if its chunk has landed.
 */
export function reset() {
  currentUser = null;
  pendingActions.length = 0;

  if (!isInitialized) return;

  run('reset', () => mixpanel.reset());
}

// NOTE: Mixpanel is intentionally NOT loaded or initialized on import. Its chunk
// is fetched, initialized and opted in only via setAnalyticsConsent(true), once
// the user has ticked the analytics consent box. This guarantees no events, no
// persisted identifiers — and now no network request for the SDK at all —
// before consent.
